import React, { useState } from 'react';
import { Alert, Box, CircularProgress, Typography } from '@mui/material';
import { useFeatureSwitchAdmin } from '../hooks/useFeatureSwitchAdmin';
import FeatureSwitchDetail from './FeatureSwitchDetail';
import FeatureSwitchList from './FeatureSwitchList';

const Notice: React.FC<{ children: React.ReactNode }> = ({ children }) => <Box sx={{ p: 2 }}>{children}</Box>;

/**
 * The Kernel view's Feature Switches tab: every switch as the server holds it in a list, and the selected
 * one in a detail pane where a platform admin turns it off, or on for selected staff groups or for everyone.
 */
const FeatureSwitchesPanel: React.FC = () => {
  const { details, availableGroups, loading, error, canSet, set } = useFeatureSwitchAdmin();
  const [selectedName, setSelectedName] = useState<string | null>(null);

  if (error) {
    return <Notice><Alert severity="error">Could not read the feature switches: {error}</Alert></Notice>;
  }
  if (loading && !details) return <Notice><CircularProgress size={24} /></Notice>;
  if (!details) {
    return <Notice><Typography variant="body2">Switch details are shown to platform admins only.</Typography></Notice>;
  }
  if (details.length === 0) {
    return <Notice><Typography variant="body2">No feature switches are declared.</Typography></Notice>;
  }

  // The first switch is open until another is picked, and is the fallback if the picked one goes away.
  const selected = details.find((d) => d.name === selectedName) ?? details[0];

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, overflow: 'hidden' }}>
      <Typography
        variant="body2"
        color="text.secondary"
        sx={{ px: 2, py: 1.5, borderBottom: 1, borderColor: 'divider' }}
      >
        Switches are held on the server and hold on every path: screens, the assistant and background
        work. Every change holds everywhere within about 15 seconds.
      </Typography>
      <Box sx={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
        <Box
          sx={{
            width: 280,
            borderRight: 1,
            borderColor: 'divider',
            display: 'flex',
            flexDirection: 'column',
            bgcolor: 'background.default',
          }}
        >
          <FeatureSwitchList details={details} selectedName={selected.name} onSelect={setSelectedName} />
        </Box>
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'auto' }}>
          <FeatureSwitchDetail
            key={selected.name}
            detail={selected}
            availableGroups={availableGroups}
            canSet={canSet}
            onSet={set}
          />
        </Box>
      </Box>
    </Box>
  );
};

export default FeatureSwitchesPanel;
